import * as React from 'react'
import { Archive, ChevronLeft } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON, patchJSON } from '@/lib/api'
import { DiscountCodeForm } from '@/pages/DiscountCodeForm'
import type { DiscountCode } from '@/pages/DiscountCodesPage'
import { useViewOnly } from '@/components/templates/app-shell'

interface DiscountCodeResponse {
  discount_code: DiscountCode
}

export function DiscountCodeDetailPage({
  discountCodeId,
  onBack,
}: {
  discountCodeId: number
  onBack: () => void
}) {
  const queryClient = useQueryClient()
  const viewOnly = useViewOnly()
  const [archiveOpen, setArchiveOpen] = React.useState(false)
  const codeQuery = useQuery({
    queryKey: ['discount-codes', discountCodeId],
    queryFn: () => getJSON<DiscountCodeResponse>(`/api/v1/discount_codes/${discountCodeId}`),
    retry: false,
  })
  const updateLifecycle = useMutation({
    mutationFn: ({ path, body }: { path: string; body: unknown }) =>
      patchJSON<DiscountCodeResponse>(path, body),
    onSuccess: async (response) => {
      queryClient.setQueryData(['discount-codes', discountCodeId], response)
      await queryClient.invalidateQueries({ queryKey: ['discount-codes'] })
      setArchiveOpen(false)
    },
  })

  if (codeQuery.isLoading) {
    return <p className="text-body-sm text-muted-foreground">Loading Discount Code...</p>
  }
  if (codeQuery.isError || !codeQuery.data) return <Alert>Discount Code unavailable</Alert>

  const code = codeQuery.data.discount_code
  const setCode = (nextCode: DiscountCode) =>
    queryClient.setQueryData(['discount-codes', discountCodeId], { discount_code: nextCode })

  return (
    <div className="w-full max-w-[1200px] px-6 py-6">
      <Button variant="outline" size="sm" onClick={onBack}>
        <ChevronLeft className="mr-1 h-4 w-4" />
        Discount Codes
      </Button>
      <header className="flex flex-wrap items-start gap-3 py-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-heading-sm font-semibold text-foreground">{code.code}</h1>
            <Badge variant={code.availability === 'Available' ? 'success' : 'warning'}>
              {code.availability}
            </Badge>
          </div>
          <p className="pt-1 text-body-sm text-muted-foreground">
            {code.redemption_count} {code.redemption_count === 1 ? 'Redemption' : 'Redemptions'}
          </p>
        </div>
        {!viewOnly && !code.archived_at && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={updateLifecycle.isPending}
              onClick={() =>
                updateLifecycle.mutate({
                  path: `/api/v1/discount_codes/${code.id}`,
                  body: { discount_code: { active: !code.active } },
                })
              }
            >
              {code.active ? 'Deactivate' : 'Reactivate'}
            </Button>
            <Button variant="quietDestructive" size="sm" onClick={() => setArchiveOpen(true)}>
              <Archive className="mr-2 h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
              Archive
            </Button>
          </div>
        )}
      </header>
      {updateLifecycle.isError && <Alert>Discount Code could not be changed</Alert>}
      <Card className="overflow-hidden">
        <CardContent className="divide-y divide-border p-0">
          {!viewOnly && <DiscountCodeForm discountCode={code} onSaved={setCode} />}
          <RedemptionsSection code={code} />
        </CardContent>
      </Card>
      {!viewOnly && (
        <ArchiveDiscountCodeDialog
          code={code}
          open={archiveOpen}
          pending={updateLifecycle.isPending}
          onOpenChange={setArchiveOpen}
          onArchive={() =>
            updateLifecycle.mutate({
              path: `/api/v1/discount_codes/${code.id}/archive`,
              body: {},
            })
          }
        />
      )}
    </div>
  )
}

function RedemptionsSection({ code }: { code: DiscountCode }) {
  return (
    <section>
      <h2 className="p-4 pb-3 text-body font-semibold text-foreground">Redemptions</h2>
      {code.redemptions.length === 0 ? (
        <p className="p-4 text-body-sm text-muted-foreground">No Redemptions yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Contact</TableHead>
              <TableHead>Redeemed</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead>Frozen reduction</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {code.redemptions.map((redemption) => (
              <TableRow key={redemption.id}>
                <TableCell className="font-medium">{redemption.contact_name}</TableCell>
                <TableCell>{redemption.redeemed_on}</TableCell>
                <TableCell>Invoice {redemption.invoice_number}</TableCell>
                <TableCell>{redemption.reduction_label}</TableCell>
                <TableCell className="text-right tabular-nums">−{redemption.reduction}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  )
}

function ArchiveDiscountCodeDialog({
  code,
  open,
  pending,
  onOpenChange,
  onArchive,
}: {
  code: DiscountCode
  open: boolean
  pending: boolean
  onOpenChange: (open: boolean) => void
  onArchive: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Archive “{code.code}”?</DialogTitle>
          <DialogDescription>
            Archived codes cannot be reactivated. Redemption history remains.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={pending} onClick={onArchive}>
            {pending ? 'Archiving...' : 'Archive Discount Code'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
